import React, { FC, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from '../../../../utils/constants/routes';
import axiosInstance from '../../../../utils/axios';
import AddEdit from '../../CommonPage/AddEdit';
import { AddBudgetForm } from './AddBudgetForm';
import { URLS } from '../../../../utils/constants/urls';
import strings from '../../../../common/Translation/Translate';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  AlertColor,
  useTheme,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import { Stack } from '@mui/system';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import DropDownInput from 'components/DropDownInput';
import InputField from 'components/InputField';
import { images } from '../../../../utils/constants/images';
import Notification from 'components/Notification';
import { formatIndianAuto } from '../../Reports/echartsConfig';
import ReportKpiCard from '../../Reports/ReportKpiCard';
import { createDropdownData } from '../../../../utils/validators/HelperFunctions';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';

function inr(value: number | string | undefined | null): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return `${getCurrencySymbol()}0`;
  return `${getCurrencySymbol()}${formatIndianAuto(n)}`;
}

function pctOfTotal(part: number, total: number): string {
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(part)) return '0%';
  const p = Math.round((part / total) * 10000) / 100;
  return `${p}%`;
}

type AllocationTone = 'low' | 'mid' | 'high' | 'over';

/** Below 10% of total: low (red), 10–90%: mid (amber), above 90%: high (green), over total: over (red). */
function allocationTone(allocated: number, total: number): AllocationTone {
  if (total <= 0) return 'mid';
  if (allocated > total) return 'over';
  const pct = (allocated / total) * 100;
  if (pct < 10) return 'low';
  if (pct <= 90) return 'mid';
  return 'high';
}

function toneColors(theme: Theme, tone: AllocationTone, active: boolean) {
  if (!active) {
    return {
      icon: theme.palette.primary.main,
      bg: theme.palette.action.hover,
      value: theme.palette.text.primary,
    };
  }
  switch (tone) {
    case 'low':
    case 'over':
      return {
        icon: theme.palette.error.main,
        bg: alpha(theme.palette.error.main, 0.12),
        value: theme.palette.error.main,
      };
    case 'mid':
      return {
        icon: theme.palette.warning.main,
        bg: alpha(theme.palette.warning.main, 0.12),
        value: theme.palette.text.primary,
      };
    case 'high':
      return {
        icon: theme.palette.success.main,
        bg: alpha(theme.palette.success.main, 0.12),
        value: theme.palette.success.dark,
      };
  }
}

type AllocationTypeApi = 'SOURCE' | 'VENUE' | 'SEGMENT' | 'MANAGER';

interface AllocationRow {
  clientKey: string;
  id?: number;
  referenceId: number | '';
  referenceName?: string;
  target: number | '';
}

function flattenVenueOptions(nodes: any[]): { label: string; value: number }[] {
  const out: { label: string; value: number }[] = [];
  for (const n of nodes || []) {
    if (n && n.id != null && n.name) {
      out.push({ label: n.name, value: n.id });
    }
    if (n?.children?.length) {
      out.push(...flattenVenueOptions(n.children));
    }
  }
  return out;
}

function sumTargets(rows: AllocationRow[]): number {
  return rows.reduce((s, r) => s + (r.target === '' ? 0 : Number(r.target) || 0), 0);
}

function rowKeysForDuplicates(rows: AllocationRow[], ref: number | ''): boolean {
  if (ref === '') return false;
  return rows.filter(r => r.referenceId === ref).length > 1;
}

/** Props for DropDownInput (untyped JS component) in strict TS builds. */
const budgetDropDownDefaults = {
  validators: [] as never[],
  onFocus: undefined as undefined,
  inputProps: undefined as undefined,
  disabled: false,
  className: undefined as undefined,
  labelId: undefined as undefined,
  style: undefined as undefined,
  sx: undefined as undefined,
};

const AddEditBudget: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams() as { id?: string };
  const [pageResponse, setPageResponse] = useState<any>({});
  const [sourceList, setSourceList] = useState<{ label: string; value: number }[]>([]);
  const [venueOptions, setVenueOptions] = useState<{ label: string; value: number }[]>([]);
  const [segmentOptions, setSegmentOptions] = useState<{ label: string; value: number }[]>([]);
  const [managerOptions, setManagerOptions] = useState<{ label: string; value: number }[]>([]);
  const [sourceRows, setSourceRows] = useState<AllocationRow[]>([]);
  const [venueRows, setVenueRows] = useState<AllocationRow[]>([]);
  const [segmentRows, setSegmentRows] = useState<AllocationRow[]>([]);
  const [managerRows, setManagerRows] = useState<AllocationRow[]>([]);
  const [sameMonth, setSameMonth] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [tab, setTab] = useState(0);
  const [budgetTotalForUi, setBudgetTotalForUi] = useState(0);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const getSourceList = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.sourcesList);
      const filter = data.map((item: any) => ({ label: item.name, value: item.id }));
      setSourceList(filter);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  const getVenues = useCallback(async () => {
    try {
      const { data, status } = await axiosInstance.get(URLS.venues);
      if (status === 200 && Array.isArray(data)) {
        setVenueOptions(flattenVenueOptions(data));
      }
    } catch {
      /* ignore */
    }
  }, []);

  const getEventTypes = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.getEventAndVenuesRfq);
      setSegmentOptions(createDropdownData(data?.eventTypes, ['id', 'name']));
    } catch {
      /* ignore */
    }
  }, []);

  const getManagers = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.budgetManagers);
      if (Array.isArray(data)) {
        setManagerOptions(createDropdownData(data, ['id', 'name']));
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    getSourceList();
    getVenues();
    getEventTypes();
    getManagers();
  }, [getSourceList, getVenues, getEventTypes, getManagers]);

  const getInitialResponse = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.editBudget(id as string));
      setSameMonth(true);
      setPageResponse({
        ...data,
        month: data.startDate,
      });
      setBudgetTotalForUi(Number(data.target) || 0);

      const unified: any[] =
        data.budgetAllocations?.length > 0
          ? data.budgetAllocations
          : (data.sourceBudgets || []).map((item: any) => ({
              id: item.id,
              allocationType: 'SOURCE' as AllocationTypeApi,
              referenceId: item.name?.value,
              referenceName: item.name?.label,
              target: item.target,
            }));

      const toRow = (r: any, prefix: string): AllocationRow => ({
        clientKey: r.id != null ? `id-${r.id}` : `${prefix}-${r.referenceId}-${Math.random()}`,
        id: r.id,
        referenceId: r.referenceId ?? '',
        referenceName: r.referenceName,
        target: r.target ?? '',
      });

      setSourceRows(
        unified
          .filter((r: any) => r.allocationType === 'SOURCE')
          .map((r: any, i: number) => toRow(r, `s-${i}`)),
      );
      setVenueRows(
        unified
          .filter((r: any) => r.allocationType === 'VENUE')
          .map((r: any, i: number) => toRow(r, `v-${i}`)),
      );
      setSegmentRows(
        unified
          .filter((r: any) => r.allocationType === 'SEGMENT')
          .map((r: any, i: number) => toRow(r, `g-${i}`)),
      );
      setManagerRows(
        unified
          .filter((r: any) => r.allocationType === 'MANAGER')
          .map((r: any, i: number) => toRow(r, `m-${i}`)),
      );
    } catch {
      /* ignore */
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getInitialResponse();
      }
    })();
  }, [getInitialResponse, id]);

  useEffect(() => {
    if (!id && pageResponse?.target != null) {
      setBudgetTotalForUi(Number(pageResponse.target) || 0);
    }
  }, [id, pageResponse?.target]);

  const sourceSum = useMemo(() => sumTargets(sourceRows), [sourceRows]);
  const venueSum = useMemo(() => sumTargets(venueRows), [venueRows]);
  const segmentSum = useMemo(() => sumTargets(segmentRows), [segmentRows]);
  const managerSum = useMemo(() => sumTargets(managerRows), [managerRows]);

  const sourceOver = sameMonth && budgetTotalForUi > 0 && sourceSum > budgetTotalForUi;
  const venueOver = sameMonth && budgetTotalForUi > 0 && venueSum > budgetTotalForUi;
  const segmentOver = sameMonth && budgetTotalForUi > 0 && segmentSum > budgetTotalForUi;
  const managerOver = sameMonth && budgetTotalForUi > 0 && managerSum > budgetTotalForUi;

  const validateRowsComplete = (rows: AllocationRow[], label: string): boolean => {
    for (const r of rows) {
      const hasRef = r.referenceId !== '' && r.referenceId != null;
      const hasT = r.target !== '' && r.target != null && String(r.target).trim() !== '';
      if (hasRef !== hasT) {
        setMessage({
          display: true,
          severity: 'error',
          message: `${label}: complete both reference and target for each row.`,
        });
        return false;
      }
    }
    return true;
  };

  const validateNoDup = (rows: AllocationRow[], label: string): boolean => {
    const ids = rows.map(r => r.referenceId).filter(x => x !== '');
    if (new Set(ids).size !== ids.length) {
      setMessage({
        display: true,
        severity: 'error',
        message: `${label}: duplicate reference is not allowed.`,
      });
      return false;
    }
    return true;
  };

  const handleCustomResponse = (custom: any) => {
    if (custom) {
      if (!custom.month) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.budget_month_required_message,
        });
        return false;
      }
      if (sameMonth && custom.target === '') {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.budget_target_required_message,
        });
        return false;
      }

      const total = Number(custom.target) || 0;
      if (!validateRowsComplete(sourceRows, strings.budget_tab_source_wise)) return false;
      if (!validateRowsComplete(venueRows, strings.budget_tab_venue_wise)) return false;
      if (!validateRowsComplete(segmentRows, strings.budget_tab_segment_wise)) return false;
      if (!validateRowsComplete(managerRows, strings.budget_tab_manager_wise)) return false;
      if (!validateNoDup(sourceRows, strings.budget_tab_source_wise)) return false;
      if (!validateNoDup(venueRows, strings.budget_tab_venue_wise)) return false;
      if (!validateNoDup(segmentRows, strings.budget_tab_segment_wise)) return false;
      if (!validateNoDup(managerRows, strings.budget_tab_manager_wise)) return false;

      if (sameMonth && total > 0 && sourceSum > total) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.budget_allocation_exceeds_total,
        });
        return false;
      }
      if (sameMonth && total > 0 && venueSum > total) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.budget_allocation_exceeds_total,
        });
        return false;
      }
      if (sameMonth && total > 0 && segmentSum > total) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.budget_allocation_exceeds_total,
        });
        return false;
      }
      if (sameMonth && total > 0 && managerSum > total) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.budget_allocation_exceeds_total,
        });
        return false;
      }

      const budgetAllocations: any[] = [];

      sourceRows.forEach(r => {
        if (r.referenceId === '' || r.target === '') return;
        const label = sourceList.find(i => i.value === r.referenceId)?.label ?? r.referenceName ?? '';
        budgetAllocations.push({
          id: r.id ?? 0,
          allocationType: 'SOURCE',
          referenceId: r.referenceId,
          referenceName: label,
          target: Number(r.target) || 0,
        });
      });

      venueRows.forEach(r => {
        if (r.referenceId === '' || r.target === '') return;
        const label = venueOptions.find(i => i.value === r.referenceId)?.label ?? r.referenceName ?? '';
        budgetAllocations.push({
          id: r.id ?? 0,
          allocationType: 'VENUE',
          referenceId: r.referenceId,
          referenceName: label,
          target: Number(r.target) || 0,
        });
      });

      segmentRows.forEach(r => {
        if (r.referenceId === '' || r.target === '') return;
        const label = segmentOptions.find(i => i.value === r.referenceId)?.label ?? r.referenceName ?? '';
        budgetAllocations.push({
          id: r.id ?? 0,
          allocationType: 'SEGMENT',
          referenceId: r.referenceId,
          referenceName: label,
          target: Number(r.target) || 0,
        });
      });

      managerRows.forEach(r => {
        if (r.referenceId === '' || r.target === '') return;
        const label = managerOptions.find(i => i.value === r.referenceId)?.label ?? r.referenceName ?? '';
        budgetAllocations.push({
          id: r.id ?? 0,
          allocationType: 'MANAGER',
          referenceId: r.referenceId,
          referenceName: label,
          target: Number(r.target) || 0,
        });
      });

      const monthDate = dayjs(custom.month);
      return {
        ...custom,
        startDate: monthDate.startOf('month').format('DD-MM-YYYY'),
        endDate: monthDate.endOf('month').format('DD-MM-YYYY'),
        target: custom.target || 0,
        budgetAllocations,
      };
    }
  };

  const onBudgetSaved = useCallback(
    (data: any) => {
      if (data?.id != null && !id) {
        navigate(`${routes.editBudget}/${data.id}`, { replace: true });
      }
    },
    [id, navigate],
  );

  const pageConfig = {
    title: id ? strings.budget_title_edit : strings.budget_title_add,
    successMessage: id ? strings.common_update_message : strings.common_save_message,
    addEditApiUrl: id ? URLS.editBudget(id) : URLS.addBudget,
    initialApi: URLS.editBudget(id as string),
    formModel: AddBudgetForm(strings, sameMonth, 3),
    navigateToPage: routes.budgetList,
    skipSuccessRedirect: true,
    isCustomResponse: true,
  };

  const onChange = (field: string, value: any, formData: any, _deleted: any, formRef?: any) => {
    if (field === 'month' && value) {
      setSameMonth(true);
      const monthDate = dayjs(value);
      formRef?.current?.handleChange(monthDate.format('MMM-YYYY'), 'name', { error: false, message: '' });
    }
    if (field === 'target') {
      setBudgetTotalForUi(Number(value) || 0);
    }
  };

  const addSourceRow = () => {
    setSourceRows(rows => [...rows, { clientKey: `new-s-${Date.now()}`, referenceId: '', target: '' }]);
  };

  const addVenueRow = () => {
    setVenueRows(rows => [...rows, { clientKey: `new-v-${Date.now()}`, referenceId: '', target: '' }]);
  };

  const addSegmentRow = () => {
    setSegmentRows(rows => [...rows, { clientKey: `new-g-${Date.now()}`, referenceId: '', target: '' }]);
  };

  const addManagerRow = () => {
    setManagerRows(rows => [...rows, { clientKey: `new-m-${Date.now()}`, referenceId: '', target: '' }]);
  };

  const updateSourceRow = (clientKey: string, patch: Partial<AllocationRow>) => {
    setSourceRows(rows => rows.map(r => (r.clientKey === clientKey ? { ...r, ...patch } : r)));
  };

  const updateVenueRow = (clientKey: string, patch: Partial<AllocationRow>) => {
    setVenueRows(rows => rows.map(r => (r.clientKey === clientKey ? { ...r, ...patch } : r)));
  };

  const updateSegmentRow = (clientKey: string, patch: Partial<AllocationRow>) => {
    setSegmentRows(rows => rows.map(r => (r.clientKey === clientKey ? { ...r, ...patch } : r)));
  };

  const updateManagerRow = (clientKey: string, patch: Partial<AllocationRow>) => {
    setManagerRows(rows => rows.map(r => (r.clientKey === clientKey ? { ...r, ...patch } : r)));
  };

  const deleteSourceRow = (clientKey: string) => {
    setSourceRows(rows => rows.filter(r => r.clientKey !== clientKey));
  };

  const deleteVenueRow = (clientKey: string) => {
    setVenueRows(rows => rows.filter(r => r.clientKey !== clientKey));
  };

  const deleteSegmentRow = (clientKey: string) => {
    setSegmentRows(rows => rows.filter(r => r.clientKey !== clientKey));
  };

  const deleteManagerRow = (clientKey: string) => {
    setManagerRows(rows => rows.filter(r => r.clientKey !== clientKey));
  };

  const updateAllocationRow = (
    kind: 'SOURCE' | 'VENUE' | 'SEGMENT' | 'MANAGER',
    clientKey: string,
    patch: Partial<AllocationRow>,
  ) => {
    switch (kind) {
      case 'SOURCE':
        updateSourceRow(clientKey, patch);
        break;
      case 'VENUE':
        updateVenueRow(clientKey, patch);
        break;
      case 'SEGMENT':
        updateSegmentRow(clientKey, patch);
        break;
      case 'MANAGER':
        updateManagerRow(clientKey, patch);
        break;
    }
  };

  const renderAllocationTable = (
    kind: 'SOURCE' | 'VENUE' | 'SEGMENT' | 'MANAGER',
    rows: AllocationRow[],
    options: { label: string; value: number }[],
    onAdd: () => void,
    addLabel: string,
    emptyCopy: string,
    columnLabel: string,
    overBudget: boolean,
  ) => {
    const total = budgetTotalForUi;
    return (
      <Box sx={{ mt: 2 }}>
        {overBudget && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {strings.budget_allocation_exceeds_total}
          </Alert>
        )}
        <Button
          variant="outlined"
          size="small"
          onClick={onAdd}
          sx={{ mb: 2 }}
          data-testid={
            kind === 'SOURCE'
              ? 'add-source-budget'
              : kind === 'VENUE'
                ? 'add-venue-budget'
                : kind === 'SEGMENT'
                  ? 'add-segment-budget'
                  : 'add-manager-budget'
          }>
          {addLabel}
        </Button>
        {rows.length === 0 ? (
          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              p: 3,
              textAlign: 'center',
              color: 'text.secondary',
            }}>
            <Typography variant="body2">{emptyCopy}</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 1.5, fontWeight: 600 }}>{columnLabel}</TableCell>
                <TableCell sx={{ py: 1.5, fontWeight: 600 }}>{strings.budget_text_target}</TableCell>
                <TableCell sx={{ minWidth: 160, py: 1.5, fontWeight: 600 }}>{strings.budget_column_completion}</TableCell>
                <TableCell align="right" sx={{ py: 1.5, fontWeight: 600 }}>
                  {strings.customermgmt_column_action}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => {
                const t = r.target === '' ? 0 : Number(r.target) || 0;
                const dup = rowKeysForDuplicates(rows, r.referenceId);
                const cellPad = { py: 1.5, verticalAlign: 'top' as const };
                return (
                  <TableRow key={r.clientKey}>
                    <TableCell sx={{ minWidth: 200, ...cellPad }}>
                      <DropDownInput
                        {...budgetDropDownDefaults}
                        label={columnLabel}
                        size="small"
                        field={`${r.clientKey}-ref`}
                        id={`${r.clientKey}-ref`}
                        inputValue={r.referenceId === '' ? '' : r.referenceId}
                        options={options}
                        placeholder={strings.budget_placeholder_select}
                        hasError={dup}
                        fieldError={dup}
                        onChange={(value: string | number) => {
                          const ref = value === '' ? '' : Number(value);
                          const label =
                            typeof ref === 'number' ? options.find(s => s.value === ref)?.label : undefined;
                          updateAllocationRow(kind, r.clientKey, { referenceId: ref, referenceName: label });
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 160, ...cellPad }}>
                      <InputField
                        label={strings.budget_text_target}
                        size="small"
                        typeValue="number"
                        field={`${r.clientKey}-target`}
                        inputValue={r.target === '' ? '' : String(r.target)}
                        inputProps={{ min: 0, step: 'any' }}
                        belowText={inr(t)}
                        textChange={value => {
                          updateAllocationRow(kind, r.clientKey, {
                            target: value === '' ? '' : Number(value),
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 160, ...cellPad }}>
                      {sameMonth && total > 0 ? (
                        <Box sx={{ py: 0.5 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, (t / total) * 100)}
                            sx={{
                              height: 8,
                              borderRadius: 1,
                              bgcolor: 'action.selected',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: 'text.secondary',
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            textAlign="right"
                            sx={{ mt: 0.5 }}>
                            {pctOfTotal(t, total)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ ...cellPad, verticalAlign: 'middle' }}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ pt: 0.5 }}>
                        <img
                          src={images.deleteIcon}
                          alt="delete"
                          width={20}
                          height={20}
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            kind === 'SOURCE'
                              ? deleteSourceRow(r.clientKey)
                              : kind === 'VENUE'
                                ? deleteVenueRow(r.clientKey)
                                : kind === 'SEGMENT'
                                  ? deleteSegmentRow(r.clientKey)
                                  : deleteManagerRow(r.clientKey)
                          }
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>
    );
  };

  const comparable = sameMonth && budgetTotalForUi > 0;
  const sourceTone = comparable ? allocationTone(sourceSum, budgetTotalForUi) : 'mid';
  const venueTone = comparable ? allocationTone(venueSum, budgetTotalForUi) : 'mid';
  const segmentTone = comparable ? allocationTone(segmentSum, budgetTotalForUi) : 'mid';
  const managerTone = comparable ? allocationTone(managerSum, budgetTotalForUi) : 'mid';
  const totalColors = toneColors(theme, 'high', false);
  const sourceColors = toneColors(theme, sourceTone, comparable);
  const venueColors = toneColors(theme, venueTone, comparable);
  const segmentColors = toneColors(theme, segmentTone, comparable);
  const managerColors = toneColors(theme, managerTone, comparable);

  const allocatedSubtitle = (allocated: number) =>
    comparable ? `${pctOfTotal(allocated, budgetTotalForUi)} of ${inr(budgetTotalForUi)}` : '—';

  const summaryCards: {
    key: string;
    title: string;
    value: ReactNode;
    subtitle?: ReactNode;
    icon: ReactNode;
    colors: ReturnType<typeof toneColors>;
    tab?: number;
  }[] = [
    {
      key: 'total',
      title: strings.budget_summary_total_target,
      value: inr(budgetTotalForUi),
      icon: <FlagOutlinedIcon sx={{ fontSize: 16 }} />,
      colors: totalColors,
    },
    {
      key: 'source',
      title: strings.budget_summary_source_allocated,
      value: comparable ? inr(sourceSum) : '—',
      subtitle: allocatedSubtitle(sourceSum),
      icon: <AccountTreeOutlinedIcon sx={{ fontSize: 16 }} />,
      colors: sourceColors,
      tab: 0,
    },
    {
      key: 'venue',
      title: strings.budget_summary_venue_allocated,
      value: comparable ? inr(venueSum) : '—',
      subtitle: allocatedSubtitle(venueSum),
      icon: <PlaceOutlinedIcon sx={{ fontSize: 16 }} />,
      colors: venueColors,
      tab: 1,
    },
    {
      key: 'segment',
      title: strings.budget_summary_segment_allocated,
      value: comparable ? inr(segmentSum) : '—',
      subtitle: allocatedSubtitle(segmentSum),
      icon: <CelebrationOutlinedIcon sx={{ fontSize: 16 }} />,
      colors: segmentColors,
      tab: 2,
    },
    {
      key: 'manager',
      title: strings.budget_summary_manager_allocated,
      value: comparable ? inr(managerSum) : '—',
      subtitle: allocatedSubtitle(managerSum),
      icon: <PersonOutlineIcon sx={{ fontSize: 16 }} />,
      colors: managerColors,
      tab: 3,
    },
  ];

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <AddEdit
        pageLoading={loading}
        handlePropsChange={onChange}
        handleCustomResponse={handleCustomResponse}
        customCallbackResponse={onBudgetSaved}
        pageConfig={pageConfig}
        pageResponse={pageResponse}>
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {strings.budget_allocation_summary}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(5, 1fr)' },
              gap: 1.5,
              mb: 2,
            }}>
            {summaryCards.map(card => (
              <ReportKpiCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={card.value}
                subtitle={card.subtitle}
                iconColor={card.colors.icon}
                iconBg={card.colors.bg}
                valueColor={card.colors.value}
                selected={card.tab != null && tab === card.tab}
                onClick={card.tab != null ? () => setTab(card.tab as number) : undefined}
              />
            ))}
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label={strings.budget_tab_source_wise} />
            <Tab label={strings.budget_tab_venue_wise} />
            <Tab label={strings.budget_tab_segment_wise} />
            <Tab label={strings.budget_tab_manager_wise} />
          </Tabs>
          <Box role="tabpanel" hidden={tab !== 0} sx={{ pt: 1 }}>
            {tab === 0 &&
              renderAllocationTable(
                'SOURCE',
                sourceRows,
                sourceList,
                addSourceRow,
                strings.add_source_budget,
                strings.budget_empty_source_allocations,
                strings.source_title_list,
                !!sourceOver,
              )}
          </Box>
          <Box role="tabpanel" hidden={tab !== 1} sx={{ pt: 1 }}>
            {tab === 1 &&
              renderAllocationTable(
                'VENUE',
                venueRows,
                venueOptions,
                addVenueRow,
                strings.add_venue_budget,
                strings.budget_empty_venue_allocations,
                strings.budget_column_venue,
                !!venueOver,
              )}
          </Box>
          <Box role="tabpanel" hidden={tab !== 2} sx={{ pt: 1 }}>
            {tab === 2 &&
              renderAllocationTable(
                'SEGMENT',
                segmentRows,
                segmentOptions,
                addSegmentRow,
                strings.add_segment_budget,
                strings.budget_empty_segment_allocations,
                strings.budget_column_event_type,
                !!segmentOver,
              )}
          </Box>
          <Box role="tabpanel" hidden={tab !== 3} sx={{ pt: 1 }}>
            {tab === 3 &&
              renderAllocationTable(
                'MANAGER',
                managerRows,
                managerOptions,
                addManagerRow,
                strings.add_manager_budget,
                strings.budget_empty_manager_allocations,
                strings.budget_column_manager,
                !!managerOver,
              )}
          </Box>
        </Box>
      </AddEdit>
    </>
  );
};

export default AddEditBudget;
